import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { drawShadowGroups } from '../../utils/drawShadowGroups';

/**
 * ShadowOverlay - zeichnet Momentschatten auf den Fabric-Canvas (#605)
 *
 * Analog GridOverlay, aber via `after:render`: der Schatten liegt über den
 * Objekten (sichtbar auch über gefüllten Flächen wie Rasen/Weg), halbtransparent
 * und nicht interaktiv — kein Fabric-Objekt, kein LayerPanel-Eintrag.
 *
 * Der `after:render`-Handler wird nur EINMAL registriert und liest die aktuellen
 * Polygone aus einem Ref. Würde man ihn bei jeder Slider-Änderung ab-/anmelden,
 * kann eine Ummeldung mitten in einer laufenden Pan-Render-Schleife einen
 * doppelt gezeichneten (hängenden) Schatten hinterlassen.
 *
 * @param {Object} props
 * @param {import('fabric').Canvas} props.canvas
 * @param {Array<{weight:number, multiPolygon:number[][][][]}>} props.groups
 * @param {number} [props.pixelsPerCm=1]
 */
const ShadowOverlay = ({ canvas, groups, pixelsPerCm }) => {
  const groupsRef = useRef(groups);

  // Handler EINMAL registrieren (stabil über Slider-Ticks/Pan hinweg)
  useEffect(() => {
    if (!canvas) return undefined;

    const draw = ({ ctx }) => {
      // Fabric feuert after:render AUCH aus renderTop() mit dem oberen
      // (Auswahl-)Canvas — dort NICHT zeichnen. Der obere Canvas wird beim Pan
      // nicht mitgelöscht, ein dort gemalter Schatten bliebe eingefroren hängen.
      if (ctx === canvas.contextTop) return;
      drawShadowGroups(ctx, canvas, groupsRef.current, { pixelsPerCm });
    };

    canvas.on('after:render', draw);
    canvas.requestRenderAll();

    return () => {
      canvas.off('after:render', draw);
      // Beim Ausschalten neu rendern, damit der Schatten verschwindet
      canvas.requestRenderAll();
    };
  }, [canvas, pixelsPerCm]);

  // Neue Geometrie in den Ref schreiben und einen Redraw anstoßen; der stabile
  // Handler zeichnet dann groupsRef.current.
  useEffect(() => {
    groupsRef.current = groups;
    canvas?.requestRenderAll();
  }, [canvas, groups]);

  return null;
};

ShadowOverlay.propTypes = {
  canvas: PropTypes.object,
  groups: PropTypes.array,
  pixelsPerCm: PropTypes.number,
};

ShadowOverlay.defaultProps = {
  canvas: null,
  groups: [],
  pixelsPerCm: 1,
};

export default ShadowOverlay;
